import { UI } from "./tokens";

// ─── Currencies ───────────────────────────────────────────────────────────────
// Symbol and label only. The fixed `rate` that used to sit here is gone: a
// conversion rate is a live figure with a date (utils/fxRates.js), and a
// money value with no live rate renders "—".
export const CURRENCIES = {
  USD: { symbol: "$", label: "דולר" },
  ILS: { symbol: "₪", label: "שקל"  },
  EUR: { symbol: "€", label: "אירו" },
  GBP: { symbol: "£", label: "פאונד" },
};

// Sector tags are identities, so they take categorical hues — never gold,
// never green/red, and never the holdings palette, which belongs to positions.
//
// The previous list collided with reserved meaning in four places: "אנרגיה"
// was hue 45°, the gold itself, "צרכנות" 8° away from it, and "רכב" 6° from
// the gain green with "פיננסים" 20° away. A sector tag that reads as profit or
// as an insight is worse than one with no colour at all.
//
// Eleven identities, eleven hues — one each, with no repeats left. The series
// used to hold six, which put אנרגיה on טכנולוגיה and תעודת סל on בריאות: a
// legend could name those pairs but nothing could separate them inside a single
// chart, and two same-coloured slices in one pie is a measurement read wrong,
// not a decoration. UI.categorical was widened to eleven for exactly this.
// "אחר" is the unclassified bucket and stays neutral by design.
export const SECTOR_COLORS = {
  "טכנולוגיה": UI.categorical[0],
  "צרכנות":    UI.categorical[1],
  "רכב":       UI.categorical[2],
  "תקשורת":    UI.categorical[3],
  "פיננסים":   UI.categorical[4],
  "בריאות":    UI.categorical[5],
  "אנרגיה":    UI.categorical[6],
  "תעשייה":    UI.categorical[7],
  "ביטחון/חלל": UI.categorical[8],
  "נדל\"ן":    UI.categorical[9],
  // Not a sector but an asset class, and it still needs to read as an identity
  // rather than as the unclassified bucket — an ETF is something we know, not
  // something we failed to resolve.
  "תעודת סל":  UI.categorical[10],
  "אחר":       UI.text.muted,
};

// מיפוי ידני ticker → סקטור. ערך פתיחה בלבד: תגית מיידית עד שהסיווג החי
// מול profile2 חוזר, והתשובה החיה דורסת אותו תמיד (App.js). הערכים כאן
// אומתו מול Finnhub החי ב-2026-08-26 בטקסונומיה המאוחדת של sectorClassify.
export const TICKER_SECTORS = {
  AAPL:    "טכנולוגיה",
  MSFT:    "טכנולוגיה",
  NVDA:    "טכנולוגיה",
  GOOGL:   "תקשורת",
  META:    "תקשורת",
  AMZN:    "צרכנות",
  TSLA:    "רכב",
  JPM:     "פיננסים",
  "BRK.B": "פיננסים",
};


// ─── TickerTape Data ──────────────────────────────────────────────────────────
// Rows with a live feed carry no number of their own. "…" is the loading
// state; once the first payload has settled, a row the feed did not fill
// shows "—" (TickerTape). A stale fixed figure used to sit here as the
// starting value, and when the fetch failed it stayed on screen looking real.
//
// Every row here has a feed. The TASE indices (ת"א 35/125, SME 60), the
// Israeli 10Y bond and DAX/FTSE/Nikkei had none — fixed figures under a
// "נתוני הדגמה" tooltip — and were removed on 2026-09-17. They come back with
// a source (ROADMAP 3.6), not before.
export const TICKER_DATA = [
  { label: "USD/ILS",    value: "…" },  // live: marketdata (intraday) → er-api
  { label: "EUR/ILS",    value: "…" },  // live: utils/fxRates (one source for all FX)
  { label: "S&P 500",    value: "…" },  // live from FRED (SP500)
  { label: "NASDAQ",     value: "…" },  // live from FRED (NASDAQCOM)
  { label: "DOW",        value: "…" },  // live from FRED (DJIA)
  { label: "ת״א 35",     value: "…" },  // live: Yahoo TA35.TA · always with the quote's time (26/10/2026)
  { label: "Oil (WTI)", value: "…" },  // live from FRED (DCOILWTICO)
  { label: "10Y UST",   value: "…" },  // live: Yahoo ^TNX → FRED (DGS10)
  { label: "VIX",       value: "…" },  // live from FRED (VIXCLS)
];

// ─── Sector → colour ──────────────────────────────────────────────────────────
// One colour per sector NAME, everywhere a sector is painted.
//
// The alternative — assigning hues by the order the sectors happen to appear in
// a given chart — means the same sector changes colour between two runs of the
// same screen, and that a legend is the only thing keeping the chart readable.
// Derive from the name and the hue is stable across renders, across screens and
// across sessions.
//
// A name outside SECTOR_COLORS — a legacy tag, or one from the geo
// taxonomy — gets a hue derived from its own characters. Stable for that name
// forever, and inside the same categorical series, so a sector tag still never
// wears gold (insight) or green/red (money).
export function sectorColor(name) {
  // No name means the sector is not determined. Neutral, never a categorical
  // hue — an unresolved holding must not read as an identity.
  if (!name) return UI.text.muted;
  if (SECTOR_COLORS[name]) return SECTOR_COLORS[name];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return UI.categorical[h % UI.categorical.length];
}


// ─── Translations ─────────────────────────────────────────────────────────────
export const TR = {
  he: {
    portfolio:"תיק", simulator:"חשבון תרגול", news:"חדשות",
    calendar:"יומן", academy:"אקדמיה", settings:"הגדרות",
    addSecurity:"+ הוסף מניה", addSecurityTitle:"הוספת מניה",
    symbolPlaceholder:"סימול (AAPL, TSLA...)", sharesPlaceholder:"כמות יחידות",
    pricePlaceholder:"שער רכישה ($)", add:"הוסף", cancel:"ביטול",
    totalPortfolio:"שווי תיק כולל", sinceInvestment:"מאז רכישה",
    remove:"הסר", close:"סגור", search:"חיפוש", lang:"EN",
    csvImport:"ייבוא מברוקר (CSV)",
    navSection:"ניווט",
    settingsAppVersion:"גרסה 2.1.2 · נתונים ממקורות חיצוניים בשם · הרשימה בכרטיס \"מאיפה הנתונים\"",
    settingsProActive:"PRO פעיל",
    settingsAccount:"חשבון",
    settingsName:"שם",
    settingsEmail:"דוא\"ל",
    settingsCurrencyLabel:"מטבע",
    settingsPlan:"תוכנית", settingsPlanFree:"חינמי",
    settingsLanguage:"שפה",
    settingsLangHe:"עברית · RTL", settingsLangEn:"English · LTR",
    settingsThemeTitle:"תצוגה",
    settingsThemeDay:"אור יום", settingsThemeNight:"לילה",
    settingsCurrencyTitle:"מטבע תצוגה",
    settingsLegal:"הצהרה משפטית",
    settingsLegalShow:"הצג הכל", settingsLegalHide:"הסתר",
    privacyLink:"מדיניות פרטיות",
    settingsLegalTitle:"אזהרה חשובה",
    settingsLegalShort:"האפליקציה Suricata מספקת מידע לצרכי מעקב ולמידה בלבד. אין בתכנים האמורים ייעוץ השקעות, שיווק השקעות, ניהול תיקי השקעות, ייעוץ מס או כל שירות פיננסי מוסדר אחר.",
    settingsLegalFull:"1. אין ייעוץ השקעות · כל המידע והנתונים באפליקציה הם לצרכי הצגה, מעקב ולמידה בלבד. אין לפרש אותם כהמלצה לקנות, למכור, להחזיק או לעסוק בכל פעולה הנוגעת לניירות ערך. האפליקציה אינה מציגה ציונים, דירוגים או תחזיות מחיר. 2. מקור הנתונים · כל נתון מגיע ממקור חיצוני בשם, ובהם Finnhub, FRED, דיווחי SEC EDGAR, Federal Register, Federal Reserve, בנק ישראל, Yahoo Finance, ExchangeRate-API ו-TradingView. הרשימה המלאה, כולל סוג הנתון מכל מקור ותדירות העדכון, מוצגת בכרטיס \"מאיפה הנתונים\" בהגדרות. סוג נתון שאינו מופיע בכרטיס אינו מוצג באפליקציה. נתון שאינו זמין מהמקור אינו מוצג, ואינו מוחלף בערך משוער. הנתונים עשויים להיות מושהים, ואינם מובטחים כמדויקים או כשלמים. מדדי הבורסות בישראל ובעולם מוצגים בעיכוב של 15 עד 30 דקות, לפי הבורסה, ושעת הנתון מוצגת לצדם. גרף המחיר מבוסס על נתוני Yahoo Finance ומוצג באמצעות ספריית Lightweight Charts™ של TradingView. סיכומים ותרגומים לעברית נוצרים באמצעות בינה מלאכותית (Google Gemini) ועלולים להכיל אי-דיוקים · המקור המצוטט הוא הקובע. 3. סיכוני השקעה · השקעה בניירות ערך כרוכה בסיכון לאובדן חלקי או מלא של הון ההשקעה. ביצועי עבר אינם מעידים על תשואות עתידיות. 4. אחריות · מפתחי האפליקציה אינם אחראים לכל נזק שייגרם כתוצאה משימוש במידע המוצג. 5. שלב הבטא · השירות נמצא בשלב בטא וניתן ללא תשלום. אין חיוב, ואין אמצעי תשלום שמור. מעבר לשירות בתשלום לא ייעשה בלי הסכמה מפורשת ונפרדת.",
    settingsAbout:"אודות",
    settingsVersion2:"גרסה", settingsUpdated:"עודכן", settingsFramework:"מסגרת",
    settingsUpdatedVal:"מאי 2026", settingsFrameworkVal:"React",
    settingsBackendNote:"הנתונים נשמרים בשרתים באיחוד האירופי. מקורות המידע מוצגים בכל כרטיס.",
    footerDisclaimer:"המידע המוצג באפליקציה זו הינו לצרכי מעקב ומידע כללי בלבד ואינו מהווה ייעוץ השקעות, ייעוץ מס או המלצה לקנייה/מכירה של ניירות ערך. ביצועי עבר אינם מבטיחים תשואה עתידית. ההחלטות הפיננסיות הן באחריות המשתמש בלבד.",
    footerAccuracy:"אין אחריות לדיוק הנתונים",
    footerPrivacy:"הנתונים שלך נשמרים בשרתים באיחוד האירופי",
    footerCopyright:"© 2026 Suricata · כל הזכויות שמורות",
  },
  en: {
    portfolio:"Portfolio", simulator:"Practice account", news:"News",
    calendar:"Calendar", academy:"Academy", settings:"Settings",
    addSecurity:"+ Add Security", addSecurityTitle:"Add Security",
    symbolPlaceholder:"Symbol (AAPL, TSLA...)", sharesPlaceholder:"Shares",
    pricePlaceholder:"Buy price ($)", add:"Add", cancel:"Cancel",
    totalPortfolio:"Total Portfolio Value", sinceInvestment:"since purchase",
    remove:"Remove", close:"Close", search:"Search", lang:"עב",
    csvImport:"Import from Broker (CSV)",
    navSection:"NAVIGATE",
    settingsAppVersion:"Version 2.1.2 · Data from named external sources · see \"Where the data comes from\"",
    settingsProActive:"PRO Active",
    settingsAccount:"Account",
    settingsName:"Name",
    settingsEmail:"Email",
    settingsCurrencyLabel:"Currency",
    settingsPlan:"Plan", settingsPlanFree:"Free",
    settingsLanguage:"Language",
    settingsLangHe:"עברית · RTL", settingsLangEn:"English · LTR",
    settingsThemeTitle:"Appearance",
    settingsThemeDay:"Daylight", settingsThemeNight:"Night",
    settingsCurrencyTitle:"Display Currency",
    settingsLegal:"Legal Disclaimer",
    settingsLegalShow:"Show full", settingsLegalHide:"Hide",
    privacyLink:"Privacy Policy",
    settingsLegalTitle:"Important Notice",
    settingsLegalShort:"Suricata provides information for tracking and educational purposes only. Nothing in this app constitutes investment advice, portfolio management, tax advice, or any other regulated financial service.",
    settingsLegalFull:"1. No investment advice — All information and data in the app are for display, tracking and educational purposes only. Do not interpret them as a recommendation to buy, sell, hold, or engage in any securities transaction. The app does not present scores, ratings or price forecasts. 2. Data sources — Every figure comes from a named external source, including Finnhub, FRED, SEC EDGAR filings, the Federal Register, the Federal Reserve, the Bank of Israel, Yahoo Finance, ExchangeRate-API and TradingView. The full list, with the type of data from each source and its update frequency, is shown in the \"Where the data comes from\" card in Settings. A type of data that is not listed in the card is not shown in the app. A figure the source does not provide is not shown, and is never replaced by an estimate. Data may be delayed and is not guaranteed to be accurate or complete. Israeli and world index figures are shown with a delay of 15 to 30 minutes, depending on the exchange, with the time of each figure beside it. The price chart is based on Yahoo Finance data and drawn with TradingView's Lightweight Charts™ library. Summaries and Hebrew translations are generated by artificial intelligence (Google Gemini) and may contain inaccuracies — the cited source prevails. 3. Investment risk — Investing in securities involves risk of partial or total loss of capital. Past performance is not indicative of future results. 4. Liability — The app developers are not liable for any damages resulting from use of the information displayed. 5. Beta — The service is in beta and provided free of charge. There is no billing and no stored payment method. A move to a paid service will not happen without separate, explicit consent.",
    settingsAbout:"About",
    settingsVersion2:"Version", settingsUpdated:"Updated", settingsFramework:"Framework",
    settingsUpdatedVal:"May 2026", settingsFrameworkVal:"React",
    settingsBackendNote:"Data is stored on servers in the EU. Data sources are shown on every card.",
    footerDisclaimer:"The information displayed in this app is for tracking and general information purposes only and does not constitute investment advice, tax advice, or a recommendation to buy or sell any securities. Past performance does not guarantee future returns. Financial decisions are the sole responsibility of the user.",
    footerAccuracy:"No warranty on data accuracy",
    footerPrivacy:"Your data is stored on servers in the EU",
    footerCopyright:"© 2026 Suricata · All rights reserved",
  },
};

